import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Scissors, ArrowRight, CheckCircle2 } from "lucide-react";
import PlatformLogo from "@/components/PlatformLogo";

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // strip accents
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { refresh } = useAuth();
  const planSlug = search.get("plan") || "";

  const [plans, setPlans] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({
    salon_name: "",
    slug: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    plan_id: "",
    industry_id: "",
    accept_terms: false,
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/public/plans").then(({ data }) => {
      setPlans(data);
      // Pre-select plan from query param
      if (planSlug) {
        const match = data.find((p) => p.slug === planSlug);
        if (match) setForm((f) => ({ ...f, plan_id: match.id }));
      }
    });
    api.get("/public/industries").then(({ data }) => {
      setIndustries(data);
      const def = data.find((i) => i.is_default) || data[0];
      if (def) setForm((f) => (f.industry_id ? f : { ...f, industry_id: def.id }));
    });
  }, [planSlug]);

  // Auto-suggest slug from salon name unless user customized it
  useEffect(() => {
    if (!slugTouched) {
      setForm((f) => ({ ...f, slug: slugify(f.salon_name) }));
    }
  }, [form.salon_name, slugTouched]);

  const [success, setSuccess] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/public/register", {
        salon_name: form.salon_name.trim(),
        slug: form.slug.trim().toLowerCase(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        owner_password: form.owner_password,
        plan_id: form.plan_id || null,
        industry_id: form.industry_id || null,
        accept_terms: form.accept_terms,
      });
      // Double-Opt-In: backend no longer returns an auth token. Show the
      // "please confirm your email" screen instead of routing to /cms.
      setSuccess({
        email: data.email || form.owner_email.trim().toLowerCase(),
        salon: data.salon,
        message: data.message,
      });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!success?.email) return;
    try {
      await api.post("/public/register/resend", { email: success.email });
      setSuccess((s) => ({ ...s, resendStatus: "ok" }));
    } catch (e2) {
      setSuccess((s) => ({ ...s, resendStatus: "err", resendErr: formatApiError(e2.response?.data?.detail) }));
    }
  };

  const selectedPlan = plans.find((p) => p.id === form.plan_id);

  return (
    <div className="min-h-screen" style={{ background: "var(--mv-bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--mv-border)", background: "rgba(253,251,247,0.85)", backdropFilter: "blur(20px)" }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/pricing" className="flex items-center gap-2">
            <PlatformLogo imgClassName="h-7 w-auto object-contain">
              <Scissors className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <span className="font-display text-lg">StudioMV</span>
            </PlatformLogo>
          </Link>
          <Link to="/login" className="mv-btn-ghost text-sm">Anmelden</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {success ? (
          <div className="mv-card p-8 md:p-10 text-center" data-testid="register-success">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center" style={{ background: "var(--mv-primary)", color: "#fff" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            </div>
            <h1 className="font-display text-3xl md:text-4xl mb-3">E-Mail bestätigen</h1>
            <p className="text-base mb-6" style={{ color: "var(--mv-text-muted)" }}>
              Wir haben einen Bestätigungs-Link an <strong>{success.email}</strong> geschickt.
              Bitte klicke darauf, um dein Konto zu aktivieren — erst danach kannst du dich einloggen.
            </p>
            <div className="text-sm rounded-xl p-4 mb-6" style={{ background: "var(--mv-surface)" }}>
              <p className="font-medium mb-1">Keine E-Mail erhalten?</p>
              <p style={{ color: "var(--mv-text-muted)" }}>
                Prüfe deinen Spam-Ordner oder fordere den Link erneut an.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button type="button" onClick={resend} className="mv-btn-secondary" data-testid="register-resend-btn">
                Bestätigungs-Mail erneut senden
              </button>
              <Link to="/login" className="mv-btn-primary" data-testid="register-to-login">
                Zum Login
              </Link>
            </div>
            {success.resendStatus === "ok" && (
              <p className="text-xs mt-4 text-green-700" data-testid="register-resend-ok">
                Neuer Bestätigungs-Link gesendet. Bitte E-Mail-Postfach prüfen.
              </p>
            )}
            {success.resendStatus === "err" && (
              <p className="text-xs mt-4 text-red-600" data-testid="register-resend-err">
                {success.resendErr || "Senden fehlgeschlagen. Bitte gleich nochmal versuchen."}
              </p>
            )}
          </div>
        ) : (
        <>
        <p className="mv-label mb-2">Kostenlos starten</p>
        <h1 className="font-display text-4xl mb-3" data-testid="register-heading">
          {(() => {
            const sel = industries.find((i) => i.id === form.industry_id);
            return sel ? `${sel.name} registrieren` : "Salon registrieren";
          })()}
        </h1>
        <p className="text-sm mb-8" style={{ color: "var(--mv-text-muted)" }}>
          In wenigen Minuten startklar — keine Kreditkarte erforderlich für die Einrichtung.
        </p>

        <form onSubmit={onSubmit} className="mv-card p-6 md:p-8 space-y-5" data-testid="register-form">
          <div>
            <p className="mv-label mb-3">Welche Art Betrieb betreibst du?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="register-industries">
              {industries.map((i) => {
                const sel = form.industry_id === i.id;
                return (
                  <button
                    type="button"
                    key={i.id}
                    data-testid={`register-industry-${i.slug}`}
                    onClick={() => setForm({ ...form, industry_id: i.id })}
                    className="text-left p-3 rounded-lg transition-colors"
                    style={{
                      background: sel ? "var(--mv-primary)" : "transparent",
                      color: sel ? "#fff" : "inherit",
                      border: "1px solid",
                      borderColor: sel ? "var(--mv-primary)" : "var(--mv-border)",
                    }}
                  >
                    <p className="text-sm font-medium">{i.name}</p>
                    <p className="text-[11px] opacity-70 mt-0.5 line-clamp-2">{i.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mv-label mb-3">Paket wählen</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {plans.map((p) => {
                const sel = form.plan_id === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`register-plan-${p.slug}`}
                    onClick={() => setForm({ ...form, plan_id: p.id })}
                    className="text-left p-3 rounded-lg transition-colors"
                    style={{
                      background: sel ? "var(--mv-primary)" : "transparent",
                      color: sel ? "#fff" : "inherit",
                      border: "1px solid",
                      borderColor: sel ? "var(--mv-primary)" : "var(--mv-border)",
                    }}
                  >
                    <p className="text-sm font-medium">{p.name}</p>
                    {p.on_sale ? (
                      <p className="text-xs mt-1">
                        <span className="font-medium">{p.sale_price.toFixed(0)} €</span>
                        <span className="line-through ml-1 opacity-70">{p.monthly_price.toFixed(0)} €</span>
                      </p>
                    ) : (
                      <p className="text-xs mt-1">{p.monthly_price.toFixed(0)} €/Monat</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1">Salon-Name</label>
              <input
                data-testid="register-salon-name"
                className="mv-input"
                placeholder="z.B. Bella Capelli"
                value={form.salon_name}
                onChange={(e) => setForm({ ...form, salon_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-xs mb-1">URL-Kürzel</label>
              <div className="flex items-center gap-2">
                <span className="text-xs whitespace-nowrap" style={{ color: "var(--mv-text-muted)" }}>/salon/</span>
                <input
                  data-testid="register-slug"
                  className="mv-input flex-1 font-mono text-sm"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") });
                  }}
                  pattern="[a-z0-9]([a-z0-9-]{0,48}[a-z0-9])?"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1">Ihr Name</label>
              <input
                data-testid="register-owner-name"
                className="mv-input"
                value={form.owner_name}
                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-xs mb-1">E-Mail</label>
              <input
                type="email"
                data-testid="register-owner-email"
                className="mv-input"
                value={form.owner_email}
                onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs mb-1">Passwort (mindestens 8 Zeichen)</label>
            <input
              type="password"
              data-testid="register-password"
              className="mv-input"
              minLength={8}
              value={form.owner_password}
              onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
              required
            />
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="register-accept-terms"
              className="mt-1"
              checked={form.accept_terms}
              onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
              required
            />
            <span style={{ color: "var(--mv-text-muted)" }}>
              Ich akzeptiere die AGB und Datenschutzbestimmungen von StudioMV.
            </span>
          </label>

          {err && (
            <p data-testid="register-error" className="text-sm px-3 py-2 rounded bg-red-50 text-red-700">{err}</p>
          )}

          <button
            type="submit"
            data-testid="register-submit"
            className="mv-btn w-full justify-center"
            disabled={busy}
          >
            {busy ? "Registriere…" : (
              <>
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                {selectedPlan && parseFloat(selectedPlan.monthly_price || 0) > 0
                  ? `${selectedPlan.name} — 14 Tage kostenlos testen`
                  : "Kostenlos starten"}
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </>
            )}
          </button>

          {selectedPlan && parseFloat(selectedPlan.monthly_price || 0) > 0 && (
            <p className="text-[11px] text-center px-2" style={{ color: "var(--mv-text-muted)" }}>
              14 Tage gratis, jederzeit kündbar. Schließt du in dieser Zeit kein kostenpflichtiges Abo ab,
              wirst du automatisch ins kostenlose <strong>Free</strong>-Paket gestuft — kein Risiko.
            </p>
          )}

          <p className="text-xs text-center" style={{ color: "var(--mv-text-muted)" }}>
            Bereits ein Konto?{" "}
            <Link to="/login" className="underline" style={{ color: "var(--mv-primary)" }}>
              Anmelden
            </Link>
          </p>
        </form>
        </>
        )}
      </main>
    </div>
  );
}
